import Vue from "vue";

function extension(file) {
  const match = /\.([^.]+)$/.exec(file.name || "");
  return match ? match[1].toLowerCase() : "";
}

function hintedFormat(file) {
  const type = (file.type || "").toLowerCase();
  const ext = extension(file);
  if (["image/heic", "image/heif"].includes(type) || ["heic", "heif"].includes(ext)) return "heic";
  if (type === "image/tiff" || ["tif", "tiff"].includes(ext)) return "tiff";
  return null;
}

async function format(file) {
  const hint = hintedFormat(file);
  if (hint) return hint;
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const text = String.fromCharCode(...bytes);
  if (text.slice(4, 8) === "ftyp" && ["heic", "heix", "mif1", "msf1"].includes(text.slice(8, 12))) return "heic";
  if (text.startsWith("II*\0") || text.startsWith("MM\0*")) return "tiff";
  return null;
}

export function reportConvertFailure({ code, type, ext, size }) {
  const details = { code, type, ext, size };
  const rollbar = Vue.prototype.$rollbar;
  if (rollbar && typeof rollbar.warning === "function") rollbar.warning("image_convert_failed", details);
  else console.warn("image_convert_failed", details);
}

function report(file, code, reporter) {
  // Сбой телеметрии не должен отменять отправку оригинала.
  try { reporter({ code, type: file.type || "", ext: extension(file), size: file.size }); } catch (e) { /* оригинал остаётся доступен */ }
}

export async function detectConvertible(file) {
  try { return Boolean(await format(file)); }
  catch (e) {
    report(file, "detect", reportConvertFailure);
    return false;
  }
}

export function loadImage(blob, deps = {}, error = "jpeg_dimensions") {
  // В браузерах без createImageBitmap размеры JPEG всё ещё доступны через Image.
  return new Promise((resolve, reject) => {
    const createUrl = deps.createObjectURL || URL.createObjectURL;
    const revokeUrl = deps.revokeObjectURL || URL.revokeObjectURL;
    const url = createUrl(blob);
    const image = deps.createImage ? deps.createImage() : new Image();
    image.onload = () => {
      revokeUrl(url);
      resolve({ image, width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => { revokeUrl(url); reject(new Error(error)); };
    image.src = url;
  });
}

const defaults = {
  createImageBitmap: (file, options) => createImageBitmap(file, options),
  createCanvas: () => document.createElement("canvas"),
  loadHeic: () => import(/* webpackIgnore: true */ `${process.env.BASE_URL || "/"}vendor/heic-to-1.6.6.js`),
  loadUtif: () => import(/* webpackChunkName: "ft206-utif2" */ "utif2"),
  loadImage,
  reporter: reportConvertFailure,
};

export function canvasAt(width, height, deps = {}, { error = "canvas", validate = true, contextOptions, releaseOnError = false } = {}) {
  if (validate && !(width > 0 && height > 0)) throw new Error("dimensions");
  const canvas = (deps.createCanvas || (() => document.createElement("canvas")))();
  canvas.width = width;
  canvas.height = height;
  try {
    const context = contextOptions ? canvas.getContext("2d", contextOptions) : canvas.getContext("2d");
    // При отказе платформы нельзя уменьшать доказательное фото (FM-03).
    if (!context || (validate && (canvas.width !== width || canvas.height !== height))) throw new Error(error);
    return { canvas, context };
  } catch (failure) {
    if (releaseOnError) { canvas.width = 0; canvas.height = 0; }
    throw failure;
  }
}

export function jpegBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob && blob.type === "image/jpeg") resolve(blob);
      else reject(new Error("jpeg_blob"));
    }, "image/jpeg", quality);
  });
}

async function fromBitmap(bitmap, quality, deps) {
  let canvas;
  try {
    const target = canvasAt(bitmap.width, bitmap.height, deps);
    canvas = target.canvas;
    target.context.drawImage(bitmap, 0, 0);
    return { blob: await jpegBlob(canvas, quality), width: bitmap.width, height: bitmap.height };
  } finally {
    if (bitmap.close) bitmap.close();
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}

class TiffFailure extends Error {}

function tiffDecodedSize(page) {
  if (!page) throw new TiffFailure("tiff_unsupported");
  const tag = (name, fallback) => page[name] ? (page[name].length === 1 ? page[name][0] : NaN) : fallback;
  const width = tag("t256"), height = tag("t257");
  const compression = tag("t259", 1), photometric = tag("t262");
  const samples = tag("t277", 1), bits = page.t258 || [1], depth = bits[0];
  // utif2 4.1.0: _decompress и toRGBA8. Остальные варианты могут молча
  // возвращать нулевые/неверные пиксели, поэтому сохраняем оригинал (FM-01).
  const rgb = photometric === 2;
  const palette = photometric === 3;
  const supported = [1, 5, 8, 32946, 32773, 7].includes(compression)
    && [0, 1, 2, 3].includes(photometric)
    && tag("t284", 1) === 1
    && (rgb ? [8, 16].includes(depth) : palette ? [1, 8].includes(depth) : [1, 8, 16].includes(depth))
    && bits.length === samples && bits.every(value => value === depth)
    && (rgb ? (samples === 3 || (samples === 4 && [1, 2].includes(tag("t338")))) : samples === 1)
    && (!page.t339 || (page.t339.length === samples && page.t339.every(value => value === 1)))
    && (!palette || (page.t320 && page.t320.length === 3 * (2 ** depth)))
    // JPEG-декодер принудительно выдаёт RGB8, без alpha и grayscale.
    && (compression !== 7 || (rgb && depth === 8 && samples === 3))
    && tag("t266", 1) === 1
    && (tag("t317", 1) === 1 || (tag("t317") === 2 && depth >= 8))
    // DNG отключает нормализацию endian для 16 бит внутри utif2.
    && !page.t33422 && !page.t50885;
  // Packed TIFF выравнивает каждую строку до целого байта.
  const size = Math.ceil(width * samples * depth / 8) * height;
  if (!supported || !Number.isSafeInteger(width) || width <= 0
      || !Number.isSafeInteger(height) || height <= 0 || !Number.isSafeInteger(size) || size <= 0) {
    throw new TiffFailure("tiff_unsupported");
  }
  return size;
}

async function fromTiff(file, quality, deps) {
  const module = await deps.loadUtif();
  const utif = module.default || module;
  const buffer = await file.arrayBuffer();
  const page = utif.decode(buffer)[0];
  const expectedSize = tiffDecodedSize(page);
  utif.decodeImage(buffer, page);
  if (!page.data || page.data.length !== expectedSize) throw new TiffFailure("tiff_data");
  const rgba = utif.toRGBA8(page);
  // toRGBA8 не снимает premultiplication. Непрозрачные RGBA безопасны,
  // полупрозрачные сохраняем в оригинале вместо повторного умножения canvas.
  if (page.t338 && page.t338[0] === 1) {
    for (let i = 3; i < rgba.length; i += 4) {
      if (rgba[i] !== 255) throw new TiffFailure("tiff_unsupported");
    }
  }
  const { width: w, height: h } = page;
  const orientation = page.t274 && page.t274[0] || 1;
  const swapped = orientation >= 5 && orientation <= 8;
  const width = swapped ? h : w;
  const height = swapped ? w : h;
  let source;
  let target;
  try {
    source = canvasAt(w, h, deps);
    const pixels = source.context.createImageData(w, h);
    pixels.data.set(rgba);
    source.context.putImageData(pixels, 0, 0);
    target = canvasAt(width, height, deps);
    // utif2 возвращает сырые пиксели: отражения и повороты TIFF он не применяет.
    const transforms = {
      1: [1, 0, 0, 1, 0, 0], 2: [-1, 0, 0, 1, w, 0],
      3: [-1, 0, 0, -1, w, h], 4: [1, 0, 0, -1, 0, h],
      5: [0, 1, 1, 0, 0, 0], 6: [0, 1, -1, 0, h, 0],
      7: [0, -1, -1, 0, h, w], 8: [0, -1, 1, 0, 0, w],
    };
    target.context.transform(...(transforms[orientation] || transforms[1]));
    target.context.drawImage(source.canvas, 0, 0);
    return { blob: await jpegBlob(target.canvas, quality), width, height };
  } finally {
    for (const entry of [source, target]) {
      if (entry) { entry.canvas.width = 0; entry.canvas.height = 0; }
    }
  }
}

export async function convertToJpeg(file, { quality = 0.92 } = {}, deps = {}) {
  const services = { ...defaults, ...deps };
  let code = "detect";
  try {
    const kind = await format(file);
    if (!kind) return null;
    let bitmap;
    try { bitmap = await services.createImageBitmap(file, { imageOrientation: "from-image" }); }
    catch (e) { /* Отказ нативного декодера ожидаем в Chromium: пробуем ленивый. */ }
    let result;
    if (bitmap) {
      code = "canvas";
      result = await fromBitmap(bitmap, quality, services);
    } else if (kind === "heic") {
      code = "heic_decode";
      const { heicTo } = await services.loadHeic();
      const blob = await heicTo({ blob: file, type: "image/jpeg", quality });
      if (!blob || blob.type !== "image/jpeg") throw new Error("jpeg_blob");
      code = "jpeg_dimensions";
      const { width, height } = await services.loadImage(blob);
      if (!(width > 0 && height > 0)) throw new Error("dimensions");
      // heicTo уже закодировал JPEG; повторный canvas здесь потерял бы качество.
      result = { blob, width, height };
    } else {
      code = "tiff_decode";
      result = await fromTiff(file, quality, services);
    }
    const name = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
    return { file: new File([result.blob], name, { type: "image/jpeg" }), width: result.width, height: result.height };
  } catch (e) {
    report(file, e instanceof TiffFailure ? e.message : code, services.reporter);
    return null;
  }
}
